'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Project {
  id: number;
  title: string;
  category: string;
  material: string;
  complexity: string;
  image: string;
  alt: string;
  beforeImage: string;
  beforeAlt: string;
  afterImage: string;
  afterAlt: string;
}

const projects: Project[] = [
{
  id: 1,
  title: 'Precision Enclosure',
  category: 'Electronics',
  material: 'Aluminum 5052',
  complexity: 'Complex Geometry',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1705f96b7-1768628392542.png",
  alt: 'Precision aluminum electronics enclosure with intricate cutouts and bent edges on inspection table',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b85a0eb2-1768628391855.png",
  beforeAlt: 'Flat aluminum sheet before fabrication process on cutting table',
  afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1df28aee9-1767689498841.png",
  afterAlt: 'Completed precision aluminum enclosure with powder coated finish and assembled components'
},
{
  id: 2,
  title: 'Structural Bracket',
  category: 'Industrial',
  material: 'Mild Steel A36',
  complexity: 'High Volume',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1509acafd-1766990360145.png",
  alt: 'Heavy duty steel structural bracket with multiple mounting holes and reinforcement ribs',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9c078c0-1768628392858.png",
  beforeAlt: 'Raw steel plate before cutting and forming operations',
  afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1796ef7c8-1768628392849.png",
  afterAlt: 'Finished structural bracket with powder coating and quality inspection markings'
},
{
  id: 3,
  title: 'Medical Device Component',
  category: 'Medical',
  material: 'Stainless Steel 316L',
  complexity: 'Tight Tolerances',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11993546c-1765183445806.png",
  alt: 'Precision stainless steel medical device component with mirror finish and tight dimensional tolerances',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d47b702a-1768628393381.png",
  beforeAlt: 'Stainless steel sheet before precision cutting and forming',
  afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_11993546c-1765183445806.png",
  afterAlt: 'Completed medical device component with polished finish and quality certification'
},
{
  id: 4,
  title: 'Decorative Panel',
  category: 'Architectural',
  material: 'Copper C110',
  complexity: 'Artistic Design',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1598ac2a2-1767940869772.png",
  alt: 'Decorative copper architectural panel with intricate laser cut pattern and patina finish',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10559d657-1768628391967.png",
  beforeAlt: 'Copper sheet before artistic laser cutting process',
  afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_17fa5486b-1765702917592.png",
  afterAlt: 'Finished decorative copper panel with artistic pattern and protective coating'
}];


export default function ProjectGallery() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [viewMode, setViewMode] = useState<'before' | 'after'>('before');
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const handleProjectClick = (project: Project) => {
    if (!isHydrated) return;
    setSelectedProject(project);
    setViewMode('before');
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setSelectedProject(null);
  };

  return (
    <section className="lg:py-24 bg-card py-[70px]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="PhotoIcon" size={20} variant="solid" className="text-secondary" />
            <span className="text-sm font-cta font-semibold text-secondary">Project Showcase</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            Fabrication Excellence in Action
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Explore our portfolio of precision fabrication projects across diverse industries and applications.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {projects.map((project) =>
          <button
            key={project.id}
            onClick={() => handleProjectClick(project)}
            className="group relative overflow-hidden rounded-lg border border-border hover:border-primary transition-all duration-base bg-background">

              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={project.image}
                alt={project.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-base" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                <div className="absolute top-4 right-4">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-base">
                    <Icon name="MagnifyingGlassPlusIcon" size={20} variant="outline" className="text-white" />
                  </div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-lg font-headline font-bold text-white mb-1">
                    {project.title}
                  </h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-200">
                    <Icon name="TagIcon" size={14} variant="outline" />
                    <span>{project.category}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Material:</span>
                  <span className="font-semibold text-foreground">{project.material}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Complexity:</span>
                  <span className="font-semibold text-accent">{project.complexity}</span>
                </div>
              </div>
            </button>
          )}
        </div>
        
        {isHydrated && selectedProject &&
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-2xl font-headline font-bold text-foreground mb-1">
                    {selectedProject.title}
                  </h3>
                  <p className="text-muted-foreground">{selectedProject.category} • {selectedProject.material}</p>
                </div>
                <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-muted rounded-md transition-colors duration-base"
                aria-label="Close modal">

                  <Icon name="XMarkIcon" size={24} variant="outline" className="text-muted-foreground" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-center space-x-4 mb-6">
                  <button
                  onClick={() => setViewMode('before')}
                  className={`px-6 py-3 rounded-md font-cta font-bold text-sm transition-all duration-base ${
                  viewMode === 'before' ?
                  'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                  }>

                    Before
                  </button>
                  <button
                  onClick={() => setViewMode('after')}
                  className={`px-6 py-3 rounded-md font-cta font-bold text-sm transition-all duration-base ${
                  viewMode === 'after' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`
                  }>

                    After
                  </button>
                </div>
                
                <div className="relative aspect-video rounded-lg overflow-hidden mb-6">
                  <AppImage
                  src={viewMode === 'before' ? selectedProject.beforeImage : selectedProject.afterImage}
                  alt={viewMode === 'before' ? selectedProject.beforeAlt : selectedProject.afterAlt}
                  className="w-full h-full object-cover" />

                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="CubeIcon" size={20} variant="outline" className="text-primary" />
                      <span className="text-sm font-semibold text-foreground">Material</span>
                    </div>
                    <p className="text-muted-foreground">{selectedProject.material}</p>
                  </div>
                  
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="ChartBarIcon" size={20} variant="outline" className="text-primary" />
                      <span className="text-sm font-semibold text-foreground">Complexity</span>
                    </div>
                    <p className="text-muted-foreground">{selectedProject.complexity}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

}